import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { login, getTranslations } from 'lib/api';
import { ArrowDownDark, Eye, EyeSlash } from '@icons';
import useDebounce from '@components/customHooks/useDebounce';
import { LoginHero, Otp } from 'components/atomic/molecules';
import { useNavigate } from 'react-router-dom-v5-compat';
import { validatePassword, validdateEmail } from 'utils/validation';
import { Form, InputGroup, Button, Alert } from 'react-bootstrap';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faCircleArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { UserContext } from 'components/contexts/UserProvider';
import { getKeycloakAvailability } from 'lib/api_ts/keycloakApi';
import MailToSupportLink from '@components/atomic/atoms/MailToSupportLink/MailToSupportLink';
import { SonarLogoSmall } from '@loghi/index';
import { useHistory } from 'react-router-dom';

// Component
const LoginForm = () =>
	// {
	// setShowLogin,
	// setApiError,
	// }
	{
		const history = useHistory();
		// Context
		const { loginContext } = useContext(UserContext);
		const [loginForm, setLoginForm] = useState({
			email: '',
			password: '',
			otp: '',
		});
		const [showPass, setShowPass] = useState(false);
		const [isLoading, setIsLoading] = useState(false);
		const [showOtp, setShowOtp] = useState(false);
		// const [showOtp, setShowOtp] = useState(true);
		const [error, setError] = useState({ show: false, message: '', title: '' });
		const userLanguage = localStorage.getItem('userLanguage') ?? 'IT';
		const navigate = useNavigate();
		const alwaysRequiredFields = useMemo(() => ['email', 'password', 'otp'], []);

		const [keycloakURL, setKeycloakURL] = useState('');

		useEffect(() => {
			(async () => {
				const keycloakRes = await getKeycloakAvailability();
				if (keycloakRes.enabled) {
					setKeycloakURL(keycloakRes.url);
				} else {
					setKeycloakURL('');
				}
			})();
		}, []);

		useDebounce(() => {
			fetchTranslations();
		});

		const [showBackendError, setShowBackendError] = useState(false);

		const fetchTranslations = useCallback(
			async (e) => {
				getTranslations()
					.then((retrievedTranslations) => {
						const filteredTranslations = retrievedTranslations.data.filter((trans) => trans.langCode === userLanguage);

						localStorage.setItem('translations', JSON.stringify(filteredTranslations));
					})
					.catch((error) => {
						// setApiError(true);
						// document.querySelector('.App').innerHTML =
						// 	`<div>${translate('common.error.backend', 'Errore di connessione col backend')}</div>`;
						setShowBackendError(true);
						console.error(translate('common.error.translations', 'Errore nel caricamento delle traduzioni'));
					});
			},
			[/*setApiError,*/ userLanguage],
		);

		// Event handlers
		const handleClick = useCallback(async () => {
			setError({ show: false, message: '', title: '' });
			// Check if loginForm is empty
			if (validdateEmail(loginForm.email) && validatePassword(loginForm.password)) {
				setIsLoading(true);
				await login(loginForm.email, loginForm.password, loginForm.otp).then(async (resp) => {
					if (resp.code === 200) {
						if (resp.data && resp.data.token) {
							loginContext(resp.data); //aggiunge info a UserContext
							// navigate('/projects');

							const hasAcceptedPrivacyPolicy = resp.data.disclaimers.some((disclaimer) => disclaimer === 'privacy_policy');

							localStorage.setItem('has_accepted_privacy_policy', `${hasAcceptedPrivacyPolicy}`);

							if (hasAcceptedPrivacyPolicy) navigate('/projects');
							else {
								setIsLoading(false);
								navigate('/#privacy-policy-to-accept');
							}
						} else {
							setError({
								show: true,
								message: resp.message,
								title: 'Successo',
							});
							setShowOtp(true);
							setIsLoading(false);
						}
					} else {
						if (resp.code === 429 && resp.message !== 'Unauthorized' && resp.message !== 'Too many requests') {
							setError({
								show: true,
								message: resp.message,
								title: 'Errore',
							});
							setShowOtp(true);
							setIsLoading(false);
						} else if (resp.code === 401 && resp.message === 'Password expired.') {
							setError({
								show: true,
								message: 'Password scaduta',
								title: 'Errore',
							});
							setIsLoading(false);
							window.location.assign('/?reset-password');
						} else if (resp.code === 422) {
							Object.keys(resp.data).map((key) => {
								// Se key è un required field, set requiredFields
								if (alwaysRequiredFields.includes(key)) {
									setError({
										show: true,
										message: resp.data[key].join(' '),
										title: 'Errore',
									});
								}
								return null;
							});
							setIsLoading(false);
						} else {
							setIsLoading(false);
							setError({
								show: true,
								message: translate('common.error.loginvalidation', 'Email o password non validi'),
								title: translate('common.error', 'Errore'),
							});
						}
					}
				});
			} else {
				setError({
					show: true,
					message: translate('common.error.loginvalidation', 'Email o password non validi'),
					title: translate('common.error', 'Errore'),
				});
			}
		}, [loginForm, loginContext, navigate, setShowOtp, alwaysRequiredFields]);

		useEffect(() => {
			if (!showOtp) return;
			if (loginForm.otp.length === 6) {
				handleClick();
			}
		}, [showOtp, handleClick, loginForm.otp]);

		if (showBackendError) {
			return (
				<div>
					<OETrans code="common.error.backend" fallback={'Errore di connessione col backend'} />
				</div>
			);
		}

		return (
			<div
				className="m-0"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(50%, 1fr))',
					height: '100%',
					justifyContent: 'center',
				}}
			>
				<LoginHero />

				<div
					className="d-flex flex-column justify-content-center"
					style={{ maxWidth: '472px', width: '100%', margin: '0 auto', padding: '1rem' }}
				>
					<Alert
						className="text-start"
						variant={error.title === 'Errore' ? 'danger' : 'success'}
						onClose={() => setError({ show: false })}
						dismissible
						show={error.show}
					>
						<Alert.Heading
							style={{
								fontSize: '15px',
								fontWeight: '600',
								lineHeight: '15.6px',
								// fontFamily: 'Barlow',
							}}
						>
							{error.title}
						</Alert.Heading>
						<p
							style={{
								fontSize: '15px',
								fontWeight: '300',
								lineHeight: '18px',
								// fontFamily: 'Barlow',
							}}
						>
							{error.message}
						</p>
					</Alert>
					<div className="d-lg-none mb-5">
						<SonarLogoSmall
							style={{
								fill: 'var(--np-text-primary-color)',
							}}
						/>
					</div>

					<div
						style={{
							marginBottom: '3rem',
						}}
					>
						<Form>
							{showOtp ? (
								<>
									<Otp
										email={loginForm.email}
										handleClick={handleClick}
										isLoading={isLoading}
										onInputChange={(otp) => {
											setLoginForm({
												...loginForm,
												otp,
											});
										}}
									/>{' '}
									{/* <LoginButton /> */}
								</>
							) : (
								<>
									<h5
										style={{
											fontSize: '32px',

											lineHeight: '28.8px',
											fontWeight: 700,
										}}
										className="mb-4"
									>
										Benvenuto in Sonar
									</h5>
									<p>Accedi per continuare</p>

									<Form.Group className="mb-4">
										<Form.Label
											htmlFor="email"
											style={{
												width: '100%',
											}}
										>
											Indirizzo e-mail
										</Form.Label>
										<Form.Control
											id="email"
											type="email"
											name="email"
											autoComplete="email"
											value={loginForm.email}
											placeholder={'Inserisci indirizzo e-mail'}
											onChange={({ target: { value } }) => setLoginForm({ ...loginForm, email: value })}
										/>
									</Form.Group>
									<Form.Group className="mb-4">
										<InputGroup>
											<Form.Label
												htmlFor="password"
												style={{
													width: '100%',
												}}
											>
												Password
											</Form.Label>
											<Form.Control
												id="password"
												autoComplete="current-password"
												isInvalid={false}
												style={{ borderRadius: '8px' }}
												{...(showPass ? { type: 'text' } : { type: 'password' })}
												name="password"
												value={loginForm.password}
												placeholder={'Inserisci password'}
												onChange={({ target: { value } }) => setLoginForm({ ...loginForm, password: value })}
											/>
											<InputGroup.Text
												style={{
													cursor: 'pointer',
													position: 'absolute',
													right: 0,
													top: '32px',
													// bottom: '32px',
													height: '38px',
													// borderColor: '#b3b3b3',
													border: 'none',
													backgroundColor: 'transparent',
												}}
											>
												{showPass ? (
													<Eye className="eye" onClick={() => setShowPass(!showPass)} />
												) : (
													<EyeSlash className="eye" onClick={() => setShowPass(!showPass)} />
												)}
											</InputGroup.Text>
										</InputGroup>
									</Form.Group>
									<Button
										disabled={isLoading}
										onClick={() => {
											// remove otp code
											setLoginForm({ ...loginForm, otp: '' });
											handleClick();
										}}
										// variant="info"
										type="button"
										className="btn custom-primary-button"
										style={{
											width: '100%',
											height: '48px',
										}}
									>
										{isLoading ? (
											<>
												<OETrans code="common.loading" fallback={'Caricamento'} />
												...
											</>
										) : (
											<OETrans code="common.login" fallback={'Accedi'} />
										)}
									</Button>

									<div>
										<div>
											<p
												style={{
													fontSize: '18px',
													fontWeight: '400',
													lineHeight: '24px',
													// fontFamily: 'Barlow',
												}}
												className="pt-3"
											>
												{/* Hai dimenticato la password?{' '} */}
												<Button
													variant="link"
													onClick={() => {
														// setShowLogin(false)
														history.push('/?reset-password');
													}}
													style={{
														// color: 'rgb(8, 162, 170)',
														fontSize: '18px',
														fontWeight: '500',
														lineHeight: '24px',
														textTransform: 'none',
														// fontFamily: 'Barlow',
													}}
													disabled={isLoading}
												>
													Password dimenticata?
												</Button>
											</p>
										</div>
									</div>

									{keycloakURL ? (
										<>
											<hr />
											<p
												style={{
													textAlign: 'center',
												}}
											>
												Oppure
											</p>

											<Button
												// variant="link"
												disabled={isLoading}
												onClick={(e) => {
													window.location = keycloakURL;
												}}
												className="btn custom-outline-button"
												style={{
													// color: 'rgb(8, 162, 170)',
													fontSize: '18px',
													fontWeight: 500,
													lineHeight: '24px',
													// fontFamily: 'Barlow',
													marginTop: '10px',
													display: 'block',
													textAlign: 'center',
													width: '100%',
													// padding: '12px 24px',
													// whiteSpace: 'nowrap',
													minHeight: '48px',
												}}
											>
												<span>Accedi con l'account dell'organizzazione</span>
											</Button>
										</>
									) : null}
								</>
							)}
						</Form>
						<p
							style={{
								marginTop: '1rem',
								textAlign: 'center',
								marginBottom: '1rem',
							}}
						>
							Per assistenza <MailToSupportLink />.
						</p>
						{showOtp ? (
							<Button
								disabled={isLoading}
								onClick={() => {
									setShowOtp(false);
								}}
								className="btn custom-ghost-button"
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
									textTransform: 'none',
									fontWeight: 500,
									marginTop: '2rem',
								}}
							>
								<ArrowDownDark
									style={{
										rotate: '90deg',
										// width: '24px',
										height: '16px',
									}}
								/>
								<span>Torna al login</span>
							</Button>
						) : null}
					</div>

					{/* {showOtp ? (
					<Button
						onClick={() => {
							setShowOtp(false);
						}}
						className="btn custom-ghost-button"
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '0.5rem',
							textTransform: 'none',
							fontWeight: 500,
							marginTop: '2rem',
						}}
					>
						<ArrowDownDark
							style={{
								rotate: '90deg',
								// width: '24px',
								height: '16px',
							}}
						/>
						<span>Torna al login</span>
					</Button>
				) : null} */}

					{/* {showOtp ? (
					<div>
						<div>
							<p
								style={{
									fontSize: '18px',
									fontWeight: '400',
									lineHeight: '24px',
									// fontFamily: 'Barlow',
								}}
								className="pt-3"
							>
								Non hai ricevuto il codice?{' '}
								<Button
									variant="link"
									disabled={isLoading}
									onClick={(e) => {
										handleClick(e);
									}}
									style={{
										color: 'rgb(8, 162, 170)',
										fontSize: '18px',
										fontWeight: '600',
										lineHeight: '24px',
										// fontFamily: 'Barlow',
									}}
								>
									Richiedi un nuovo codice
								</Button>
							</p>
						</div>
					</div>
				) : (
					<div>
						<div>
							<p
								style={{
									fontSize: '18px',
									fontWeight: '400',
									lineHeight: '24px',
									// fontFamily: 'Barlow',
								}}
								className="pt-3"
							>
								Hai dimenticato la password?{' '}
								<Button
									variant="link"
									onClick={() => setShowLogin(false)}
									style={{
										color: 'rgb(8, 162, 170)',
										fontSize: '18px',
										fontWeight: '500',
										lineHeight: '24px',
										// fontFamily: 'Barlow',
									}}
									disabled={isLoading}
								>
									Recupera qui
								</Button>
							</p>
						</div>
					</div>
				)} */}
				</div>
			</div>
		);
	};

export default LoginForm;
